import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { Link as Hyperlink } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useUser } from "../contexts/useUser";
import { updateInvitedLLMUser } from "../dataaccess/updateInvitedLLMUser";
import { createNewLLMUserAndLLMProject } from "../dataaccess/createNewLLMUserAndLLMProject";
import { API } from "aws-amplify";

interface WelcomeProps {
  addNew?: boolean;
}

export default function Welcome(props: WelcomeProps) {
  const { user: cognitoUser } = useAuthenticator((context) => [context.user]);
  const { user, setUser } = useUser();

  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");
  const [acceptTofS, setAcceptTofS] = useState(false);
  const [acceptTofSError, setAcceptTofSError] = useState("");
  const [acceptGDPR, setAcceptGDPR] = useState(false);
  const [acceptGDPRError, setAcceptGDPRError] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectNameError, setProjectNameError] = useState("");

  const createClick = async () => {
    let valid = true;
    if (displayName === "") {
      setDisplayNameError("Please enter your display name");
      valid = false;
    }

    if (!acceptTofS) {
      setAcceptTofSError("Please accept the Terms of Service");
      valid = false;
    }

    if (!acceptGDPR) {
      setAcceptGDPRError(
        "Please accept the Privacy & GDPR (for users in the European Union) agreements"
      );
      valid = false;
    }

    if ((!user || props.addNew) && projectName === "") {
      setProjectNameError("Please enter your project/organization name");
      valid = false;
    }

    if (!valid) {
      return;
    }

    if (user && !props.addNew) {
      // pre-invited user
      try {
        const updatedUser = await updateInvitedLLMUser(
          user?.id,
          displayName,
          acceptTofS,
          acceptGDPR
        );

        setUser(updatedUser);
        sendNewUserAlert(
          user?.email ?? "unknown",
          displayName,
          projectName,
          updatedUser.projectId
        );
        navigate("/", { replace: true });
      } catch (err) {
        navigate("/", { replace: true });
      }
    } else {
      // first time user no project
      try {
        const updatedUser = await createNewLLMUserAndLLMProject(
          cognitoUser.attributes?.email
            ? cognitoUser.attributes?.email.toLowerCase()
            : "",
          projectName,
          displayName,
          acceptTofS,
          acceptGDPR
        );

        if (updatedUser) {
          setUser(updatedUser);
          sendNewUserAlert(
            cognitoUser.attributes?.email
              ? cognitoUser.attributes?.email.toLowerCase()
              : "",
            displayName ?? "unknown",
            projectName ?? "unknown",
            updatedUser.projectId
          );

          navigate("/", { replace: true });
        }
      } catch (err) {
        navigate("/", { replace: true });
      }
    }
  };

  const sendNewUserAlert = async (
    email: string,
    displayName: string,
    projectName: string,
    projectId: string
  ) => {
    try {
      const response = await API.post("eventapi", "/newProjectOrUser", {
        body: { email, displayName, projectName, projectId },
      });
      console.log("New user alert sent successfully:", response);
    } catch (error) {
      console.error(
        "sendNewUserAlert failed, continuing without blocking user:",
        error
      );
    }
  };

  return (
    <>
      <Toolbar />
      <Toolbar />
      <Container sx={{ p: 2 }} maxWidth="sm">
        <Card>
          <CardHeader
            title={
              user && !props.addNew
                ? `Welcome - join an existing project by invitation` //${user?.project?.name || "not named"}
                : "Welcome - create a new company / project"
            }
          />
          <CardContent>
            <FormControl fullWidth>
              <TextField
                fullWidth
                name="displayNameInput"
                sx={{ p: 1 }}
                label="Your name"
                variant="standard"
                error={displayNameError !== ""}
                helperText={
                  displayNameError !== ""
                    ? displayNameError
                    : "Enter your name as you would like it to appear in the app"
                }
                value={displayName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setDisplayNameError("");
                  setDisplayName(event.target.value);
                }}
              />
            </FormControl>
            {(!user || props.addNew) && (
              <FormControl fullWidth sx={{ p: 1 }}>
                <TextField
                  id="project_name"
                  label="Company Name"
                  variant="standard"
                  error={projectNameError !== ""}
                  helperText={
                    projectNameError !== ""
                      ? projectNameError
                      : "This is the name of your organization or product. It is where all your settings and analytics will be grouped."
                  }
                  value={projectName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setProjectNameError("");
                    setProjectName(event.target.value);
                  }}
                />
              </FormControl>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={acceptTofS}
                  onChange={() => {
                    setAcceptTofS((prior) => !prior);
                    setAcceptTofSError("");
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  Please review and accept our{" "}
                  <Hyperlink
                    target="_new"
                    referrerPolicy="no-referrer"
                    rel="noopener noreferrer"
                    href="https://www.llmasaservice.io/terms-of-service/"
                  >
                    Terms of Service policy
                  </Hyperlink>
                  .
                </Typography>
              }
            />{" "}
            {acceptTofSError !== "" ? (
              <Typography variant="body2" color="error">
                {acceptTofSError}
              </Typography>
            ) : null}
            <FormControlLabel
              control={
                <Switch
                  checked={acceptGDPR}
                  onChange={() => {
                    setAcceptGDPR((prior) => !prior);
                    setAcceptGDPRError("");
                  }}
                />
              }
              value={acceptGDPR}
              label={
                <Typography variant="body2">
                  Please review and accept our{" "}
                  <Hyperlink
                    target="_new"
                    referrerPolicy="no-referrer"
                    rel="noopener noreferrer"
                    href="https://www.llmasaservice.io/privacy/"
                  >
                    Privacy and data use policy including GDPR compliance
                  </Hyperlink>
                  .
                </Typography>
              }
            />{" "}
            {acceptGDPRError !== "" ? (
              <Typography variant="body2" color="error">
                {acceptGDPRError}
              </Typography>
            ) : null}
            {props.addNew ? (
              <Typography variant="body2">
                <br /> Note: Billing subscriptions is per project.
              </Typography>
            ) : null}
          </CardContent>

          <CardActions>
            <Button
              disabled={creating}
              variant="contained"
              onClick={() => {
                setCreating(true);
                createClick();
                setCreating(false);
              }}
            >
              Save and continue
            </Button>
          </CardActions>
        </Card>
      </Container>
    </>
  );
}
